/*
 * Waiver flow — pure model (canon §6 `waiver.flow.*`, FLEXIBLE).
 * Gate/block/receipt strings are the canon [chrome] templates VERBATIM;
 * consent state is per-SECTION only (canon §6 must-not: no per-clause ticks,
 * no single global "I agree"). All functions are pure and unit-tested.
 */
import { translate, type TranslateFn } from '../../i18n/useLocale'
import type { WaiverDocument, WaiverTemplate } from '../../data/waiverTemplate'

/** Default resolver — English, so the pure functions stay directly assertable
 *  in unit tests AND the printable legal record stays in the language it was
 *  signed in; the screen passes the live locale's t (same pattern as
 *  wizardModel). */
const enT: TranslateFn = (k, p) => translate('en', k, p)

/** Signing subject. The guardian entry is WIRED (child-holder launch-gate
 *  slice): WaiverFlow resolves it from Get-ready's per-holding route state
 *  (holderKind='child' + holderName) or the caller's live holdings — the
 *  guardian signs the child's waiver in their OWN name (honest audit; the
 *  chrome + doc-set structure below keys off the subject, canon §6). */
export type WaiverSubject =
  | { kind: 'self' }
  | { kind: 'guardian'; who: string }

/** Per-section consent scratch key — `{docId}.{sectionIndex}`. Scratch is
 *  scoped per event and reset on submit/exit (canon §6 signed-record rule:
 *  "never carried between agreements"). */
export const sectionKey = (docId: string, index: number): string => `${docId}.${index}`

/**
 * Fill the signed-in participant's name into any `{{participant_name}}`
 * placeholders the resolver left for the CLIENT (BOARDROOM-2026-08-13-110303).
 * The org/event tokens ({{event_name}}/{{org_name}}/{{org_unit_name}}) are
 * resolved SERVER-SIDE by settings — the participant's identity is client-only,
 * so this one token is the FE's to fill so ZERO literal `{{...}}` reach the DOM.
 * Tolerant of internal whitespace + case. An empty name (no profile yet) falls
 * back to a neutral "the participant" rather than leaving a broken gap — never
 * fabricates a specific person's name.
 */
export function fillParticipant(text: string, participantName: string): string {
  const name = participantName.trim() || 'the participant'
  return text.replace(/\{\{\s*participant_name\s*\}\}/gi, name)
}

export type ConsentState = Record<string, boolean>

/** Count of REQUIRED sections in a doc not yet ticked. */
export function requiredLeft(doc: WaiverDocument, consents: ConsentState): number {
  return doc.sections.filter((s, i) => s.required && !consents[sectionKey(doc.id, i)]).length
}

/** A doc gates Continue on its required sections only — the optional section
 *  never blocks (canon §6 acceptance). */
export function docOk(doc: WaiverDocument, consents: ConsentState): boolean {
  return requiredLeft(doc, consents) === 0
}

/** Optional sections left unticked across ALL docs — the receipt's
 *  `Declined: {names}` list. */
export function declinedOptional(documents: WaiverDocument[], consents: ConsentState): string[] {
  const declined: string[] = []
  for (const d of documents) {
    d.sections.forEach((s, i) => {
      // Only sections that actually offer an OPTIONAL consent tick (organiser
      // authored consent text, not required) can be "declined". Read-only
      // sections of a typed-signature template have no tick, so they are never
      // declined — they're accepted by the signature.
      if (!s.required && s.consent && !consents[sectionKey(d.id, i)]) declined.push(s.name)
    })
  }
  return declined
}

/** Gate line above the sections [chrome]:
 *  `{N} section{s} still to accept` → `✓ All required sections accepted`. */
export function gateText(reqLeft: number, tr: TranslateFn = enT): string {
  if (reqLeft === 0) return tr('waiver.gate.ok')
  return reqLeft === 1 ? tr('waiver.gate.left.one') : tr('waiver.gate.left.other', { n: reqLeft })
}

/** Doc block bar [chrome]: `Accept the {n} remaining required section{s} to continue`. */
export function docBlockText(reqLeft: number, tr: TranslateFn = enT): string {
  return reqLeft === 1 ? tr('waiver.block.doc.one') : tr('waiver.block.doc.other', { n: reqLeft })
}

/**
 * Signature match — INDEPENDENT MUST-FIX (BOARDROOM-2026-08-09-133534): the
 * typed signature must equal the participant's PROFILE NAME case-insensitively.
 * BOTH sides are normalised (trim + locale-aware lowercase) before comparing —
 * the previous gate accepted any ≥2-char string; this one accepts ONLY the
 * profile name (canon §6 must-not: the input must not accept a non-matching name).
 */
export function normaliseSignature(name: string): string {
  return name.trim().toLocaleLowerCase()
}

export function signatureMatches(typed: string, expected: string): boolean {
  const t = normaliseSignature(typed)
  return t.length > 0 && t === normaliseSignature(expected)
}

/** Sign-screen hint [chrome]: `Must match {name} exactly` →
 *  `✓ Signature matches your profile name` / `Doesn't match {name}`. */
export function signHint(typed: string, expected: string, tr: TranslateFn = enT): { text: string; tone: 'idle' | 'ok' | 'bad' } {
  if (!typed.trim()) return { text: tr('waiver.sign.hint.must', { name: expected }), tone: 'idle' }
  return signatureMatches(typed, expected)
    ? { text: tr('waiver.sign.hint.ok'), tone: 'ok' }
    : { text: tr('waiver.sign.hint.bad', { name: expected }), tone: 'bad' }
}

/** Sign block bar [chrome]: `Type your full name to sign` /
 *  `Your signature must match {name} exactly`. */
export function signBlockText(typed: string, expected: string, tr: TranslateFn = enT): string {
  return typed.trim() ? tr('waiver.block.sign.match', { name: expected }) : tr('waiver.block.sign.empty')
}

/** Doc intro [chrome]. Read-only re-open swaps to the signed-record intro
 *  (canon §6 signed-record rule). */
export function docIntro(
  doc: WaiverDocument,
  subject: WaiverSubject,
  organiser: string,
  signed: boolean,
  tr: TranslateFn = enT,
): string {
  // Self-subject party word resolves through the resource (P5 review
  // follow-up: the literal 'you' was spliced into translated intro strings).
  const party = subject.kind === 'guardian' ? subject.who : tr('waiver.party.you')
  if (signed) return tr('waiver.intro.signedDoc', { party, org: organiser })
  return doc.intro ?? tr('waiver.intro.doc', { party, org: organiser })
}

/** Sign-screen chrome strings [chrome] (guardian signs THEIR OWN name). */
export function signChrome(subject: WaiverSubject, who: string, tr: TranslateFn = enT): {
  heading: string
  intro: string
  signedFor: string
  label: string
} {
  if (subject.kind === 'guardian') {
    return {
      heading: tr('waiver.sign.heading.guardian'),
      intro: tr('waiver.sign.intro.guardian', { who }),
      signedFor: tr('waiver.sign.for.guardian', { who }),
      label: tr('waiver.sign.label.guardian'),
    }
  }
  return {
    heading: tr('waiver.sign.heading.self'),
    intro: tr('waiver.sign.intro.self'),
    signedFor: tr('waiver.sign.for.self', { who }),
    label: tr('waiver.sign.label.self'),
  }
}

/** `{title} {version}` list, `·`-joined — receipt `Documents` row + the
 *  templateVersion carried to my-api. */
export function docsLine(docs: Array<{ title: string; version: string }>): string {
  return docs.map((d) => `${d.title} ${d.version}`).join(' · ')
}

/** Display stamp, transcribed from the DC: `{d} {Mon} {yyyy}, {HH}:{MM}`. */
export function formatStamp(now: Date): string {
  const MON = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${now.getDate()} ${MON[now.getMonth()]} ${now.getFullYear()}, ${pad(now.getHours())}:${pad(now.getMinutes())}`
}

/** Receipt rows [chrome] — keys are the canon-verbatim `data-k` values. */
export interface ReceiptRow { k: string; v: string }
export function receiptRows(record: {
  guardian: boolean
  participant: string
  signedBy: string
  when: string
  docs: Array<{ title: string; version: string }>
  declined: string[]
}, tr: TranslateFn = enT): ReceiptRow[] {
  return [
    { k: tr('waiver.receipt.k.documents'), v: docsLine(record.docs) },
    { k: tr('waiver.receipt.k.participant'), v: record.participant },
    { k: tr(record.guardian ? 'waiver.receipt.k.signedByGuardian' : 'waiver.receipt.k.signedBy'), v: record.signedBy },
    { k: tr('waiver.receipt.k.signedAt'), v: record.when },
    {
      k: tr('waiver.receipt.k.optional'),
      v: record.declined.length
        ? tr('waiver.receipt.declined', { list: record.declined.join(', ') })
        : tr('waiver.receipt.allAccepted'),
    },
  ]
}

/**
 * `Signed on {date} at {time}` — the unmistakable signature line shown at the
 * foot of a read-only re-opened agreement and on the printable receipt
 * (CMT-2026-08-13-121331). Derived from the stored display stamp
 * (`{d} {Mon} {yyyy}, {HH}:{MM}` — see formatStamp); falls back gracefully if a
 * record's stamp is not in the `date, time` shape. Never fabricates a time.
 */
export function signedOnLine(record: { when: string }, tr: TranslateFn = enT): string {
  const idx = record.when.lastIndexOf(', ')
  if (idx < 0) return tr('waiver.signedOnBare', { when: record.when })
  const date = record.when.slice(0, idx)
  const time = record.when.slice(idx + 2)
  return tr('waiver.signedOn', { date, time })
}

/**
 * Event-banner resolution CASCADE for the signed-waiver print view (Duncan
 * design gap BOARDROOM-134838 / MY-140426 — the banner rendered ONLY for the
 * single EVENT_BANNER_URL hardcode). Pure precedence, unit-pinned:
 *   1. `banner_desktop_url` from settings' RESOLVED waiver-template payload
 *      (the durable fix named in 8d87c21 — settings does not emit it yet, so
 *      absent today), else
 *   2. the org-branding resolved `heroImageUrl` the app already fetches/caches
 *      (DataProvider → orgBranding, BOARDROOM-2026-08-28-110128), else
 *   3. the legacy per-event EVENT_BANNER_URL map entry (interim hardcode), else
 *   4. undefined — buildReceiptPrintHtml degrades to the organiser wordmark
 *      (today's no-banner behaviour, never a broken image).
 * HONEST DEGRADATION: every input is an already-resolved value (no fetch runs
 * here), so an errored/404'd tier arrives as null/undefined and simply falls
 * through — a banner can never block printing. Blank/whitespace strings fall
 * through too (never a broken <img src="">).
 */
export function resolveEventBannerUrl(candidates: {
  /** Tier 1 — resolved waiver-template payload banner (bannerDesktopUrl). */
  templateBannerUrl?: string | null
  /** Tier 2 — org-branding resolved hero (heroImageUrl); null = unset/404,
   *  undefined = branding not fetched / fetch failed — both fall through. */
  brandingHeroUrl?: string | null
  /** Tier 3 — legacy EVENT_BANNER_URL map entry for this event. */
  legacyBannerUrl?: string | null
}): string | undefined {
  const pick = (v: string | null | undefined): string | undefined => {
    const s = typeof v === 'string' ? v.trim() : ''
    return s || undefined
  }
  return (
    pick(candidates.templateBannerUrl)
    ?? pick(candidates.brandingHeroUrl)
    ?? pick(candidates.legacyBannerUrl)
  )
}

/** Stable grep tag for the leg-4 degradation beacon (BOARDROOM-2026-09-05-101523). */
export const WAIVER_BANNER_DEGRADED_TAG = '[waiver][banner-degraded]'

export type BannerDegradationReason =
  /** The orders wire had not finished loading at print time — an eventById
   *  miss proves NOTHING about the event (the events list is empty while the
   *  read is in flight). Never classified as 'event-not-in-orders-wire'. */
  | 'orders-wire-loading'
  /** The orders wire was not readable at print time (fetch failed →
   *  'unavailable', or the service is not configured in this build) — event
   *  absence is equally unproven. */
  | 'orders-wire-unavailable'
  /** eventById(eventId) missed on a SETTLED ('ready') wire — the event is NOT
   *  on the purchaser-email-keyed box-office orders wire (a claim/reassign
   *  holder's session), so the org unit was never even resolvable. The org MAY
   *  have a hero configured. */
  | 'event-not-in-orders-wire'
  /** The order row carried no real organiserId UUID (api.ts synthesises
   *  `org-{name}`), so the branding read could not be keyed. */
  | 'org-unit-id-not-on-wire'
  /** The org unit was known but its branding read failed / had not landed at
   *  print time (DataProvider caches undefined for unfetched/failed). */
  | 'org-branding-unfetched-or-failed'
  /** The branding read LANDED as a 404 (DataProvider caches null): the wire
   *  named an org-unit UUID that settings does not know — an unknown or
   *  mis-provisioned org unit, NOT proof the org configured no banner (its
   *  branding record was never readable). Distinct from a RESOLVED record
   *  with heroImageUrl null, which IS proven honest absence and stays silent. */
  | 'org-unit-unknown-or-misprovisioned'

/** The orders-wire readiness vocabulary (DataProvider's liveState — kept
 *  structurally identical to api.ts LiveDataState so the component threads it
 *  straight through without a data-layer import cycle). */
export type OrdersWireState = 'unconfigured' | 'loading' | 'ready' | 'unavailable'

/**
 * Leg-4 degradation DIAGNOSIS (BOARDROOM-2026-09-05-101523 hardening): when the
 * print-banner cascade lands on the wordmark (tier 4), classify WHY — so the
 * silent fall-through that made the missing-banner complaint recur is now
 * observable. Pure + unit-pinned. Returns a reason ONLY for states where banner
 * data may exist upstream but was NOT resolvable at print time; returns null
 * (stay silent) when:
 *   - there is no data context at all (component-test-only state — production
 *     always mounts WaiverFlow inside DataProvider, see App.tsx), or
 *   - branding RESOLVED as a RECORD (possibly heroless) — PROVEN honest
 *     absence, the genuinely-unconfigured case that must never warn.
 * DESIGN CHOICE (review round 2, loading-state): while the orders wire is
 * still loading/unreadable the beacon EMITS a dedicated reason rather than
 * being suppressed — suppression would recreate the exact silent fall-through
 * the beacon exists to eliminate (a print during load still degraded, and a
 * field report should carry that), while the dedicated reason keeps
 * 'event-not-in-orders-wire' trustworthy: it is now asserted ONLY against a
 * settled wire.
 * Diagnosis only — it never blocks printing (the caller degrades identically
 * either way) and never asserts the upstream data exists (PROCESS-3: it says
 * "unresolvable", not "misconfigured").
 */
export function diagnoseBannerDegradation(ctx: {
  hasDataContext: boolean
  /** DataProvider liveState at print time — 'ready' is the ONLY state in
   *  which an eventById miss is evidence of wire absence. */
  ordersWireState: OrdersWireState
  eventFound: boolean
  orgUnitIsUuid: boolean
  /** DataProvider cache entry: undefined = unfetched/failed; null = org-unit
   *  404; a record with heroImageUrl null = resolved-heroless. */
  branding: { heroImageUrl: string | null } | null | undefined
}): BannerDegradationReason | null {
  if (!ctx.hasDataContext) return null
  if (!ctx.eventFound) {
    if (ctx.ordersWireState === 'loading') return 'orders-wire-loading'
    if (ctx.ordersWireState !== 'ready') return 'orders-wire-unavailable'
    return 'event-not-in-orders-wire'
  }
  if (!ctx.orgUnitIsUuid) return 'org-unit-id-not-on-wire'
  if (ctx.branding === undefined) return 'org-branding-unfetched-or-failed'
  if (ctx.branding === null) return 'org-unit-unknown-or-misprovisioned'
  return null
}

/**
 * CMT-2026-09-03-024 (Duncan spec, BOARDROOM-2026-09-03-055847) — reserved
 * footer-band geometry for the printed waiver. BAND = the visible fixed band
 * (Trumin mark bottom-right + sponsor slots); SPACE = the per-page vertical
 * space the pagination engine reserves for it (the repeated tfoot spacer +
 * the body's bottom padding). INVARIANT (unit-pinned): SPACE >= BAND, so the
 * painted band always lies wholly inside the reserved zone and body text can
 * never collide with it — at any content length, on every page.
 */
export const WAIVER_PRINT_BAND_PX = 72
export const WAIVER_PRINT_BAND_SPACE_PX = 88

/** Minimal HTML escape for values interpolated into the print document. */
function esc(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
}

/**
 * Build a self-contained, printable HTML document for the signed waiver — the
 * "Download the signed PDF" action prints THIS to PDF (no network, no external
 * library: the offline/CSP posture forbids CDN fetches). Pure + unit-tested; the
 * impure window/print step lives in the component.
 *
 * CMT-2026-08-14-032 (CRITICAL): the download used to be a bare RECEIPT STUB —
 * just the doc titles + signature evidence, none of the actual legal text the
 * signer agreed to, and no branding. It is now a proper branded legal document:
 *   (1) BRANDING — organiser wordmark + event, matched to the branded look. The
 *       logo is a wordmark (not a remote <img>) on purpose: the print window is
 *       about:blank and the CSP/offline posture forbids a CDN/remote fetch; an
 *       actual raster logo would have to arrive as an embedded data-URI (settings
 *       provides the asset) — a follow-up, tracked in the completion.
 *   (2) LEGAL BODY — the full acknowledged document tree (documents → sections →
 *       clauses WITH bodies + guardian declaration), rendered with {{participant}}
 *       filled exactly as shown on screen. This is the EXACT text the signer read
 *       and accepted (the on-screen template IS settings' resolved template via
 *       the waiverTemplate seam), so nothing is fabricated
 *       (PROGRAMME-AD-NO-FABRICATED-DATA-IN-BUILDS) and no extra network hop is
 *       needed in the print path. Each section carries its ACCEPTED / DECLINED
 *       state from the stored record.
 *   (3) RECEIPT + SIGNATURE — retained verbatim (participant, signed-by, stamp,
 *       optional consents, script-font signature).
 * `documents`/`who` are optional so an older caller (or a record with no template
 * on hand) still renders the receipt-only form rather than crashing.
 */
export function buildReceiptPrintHtml(
  record: {
    guardian: boolean
    participant: string
    signedBy: string
    when: string
    docs: Array<{ title: string; version: string }>
    declined: string[]
  },
  opts: {
    event?: string
    organiser?: string
    documents?: WaiverDocument[]
    who?: string
    /** CMT-2026-08-15-011: the EVENT BANNER (OnePulse) shown across the TOP,
     *  replacing the Trumin wordmark (wrong mark for an event doc). A central-R2 /
     *  org_units brand-pack URL; the print window loads it remotely (fresh
     *  about:blank window, not under the app CSP). Absent → graceful fallback to
     *  the organiser wordmark text (never a broken image). */
    eventBannerUrl?: string
    /** The small Trumin LOGO shown BOTTOM-RIGHT on every printed page (central-R2
     *  wordmark) — since CMT-2026-09-03-024 it sits INSIDE the reserved footer
     *  band, not as a bare overlay. Absent → omitted. */
    truminLogoUrl?: string
    /** CMT-2026-09-03-024: designed SPONSOR-LOGO SLOTS in the reserved footer
     *  band — a left-aligned row, max 3, Trumin mark keeping the right corner.
     *  Assets come later: blank/absent entries are dropped (never a broken
     *  <img>), so today the slots are simply hidden. */
    sponsorLogoUrls?: Array<string | null | undefined>
  } = {},
): string {
  const who = opts.who || record.participant
  const rows = receiptRows(record)
    .map(
      (r) =>
        `<tr><th scope="row">${esc(r.k)}</th><td>${esc(r.v)}</td></tr>`,
    )
    .join('')
  const header = [opts.organiser, opts.event]
    .filter((v): v is string => !!v)
    .map(esc)
    .join(' · ')

  // CMT-2026-09-03-024 sponsor slots — designed layout, hidden until assets
  // exist. Blank/null entries are dropped (never a broken <img>), capped at 3.
  const sponsors = (opts.sponsorLogoUrls ?? [])
    .map((u) => (typeof u === 'string' ? u.trim() : ''))
    .filter(Boolean)
    .slice(0, 3)
    .map((u, i) => `<img class="sp" src="${esc(u)}" alt="Sponsor ${i + 1}">`)
    .join('')

  // (2) Full acknowledged legal body — the actual agreement, not just titles.
  const legal = (opts.documents ?? [])
    .map((doc) => {
      const sections = doc.sections
        .map((s) => {
          const accepted = recordTick(s, record.declined)
          const state = s.required
            ? `<span class="state ok">Accepted — required</span>`
            : accepted
              ? `<span class="state ok">Accepted</span>`
              : `<span class="state no">Declined — optional</span>`
          const clauses = s.clauses
            .map(
              (c) =>
                `<div class="cl${c.sub ? ' sub' : ''}"><span class="n">${esc(c.num)}</span>`
                + `<span class="t">${esc(fillParticipant(c.text, who))}</span></div>`,
            )
            .join('')
          const note = s.note ? `<p class="note">${esc(fillParticipant(s.note, who))}</p>` : ''
          return `<section class="sec"><h3>${esc(fillParticipant(s.name, who))} ${state}</h3>${clauses}${note}</section>`
        })
        .join('')
      return `<article class="doc"><p class="eyebrow">${esc(doc.version)}</p>`
        + `<h2>${esc(fillParticipant(doc.title, who))}</h2>${sections}</article>`
    })
    .join('')

  const title = legal ? 'Signed waiver' : 'Signed waiver — receipt'
  return `<!doctype html><html lang="en"><head><meta charset="utf-8">`
    + `<meta name="viewport" content="width=device-width, initial-scale=1">`
    + `<title>${esc(title)}</title><style>`
    + `*{box-sizing:border-box}`
    + `body{font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,Helvetica,Arial,sans-serif;`
    // Bottom padding >= the footer band (CMT-024) so on SCREEN the document
    // tail can always scroll clear of the fixed band.
    + `color:#111;margin:0;padding:32px 32px ${WAIVER_PRINT_BAND_SPACE_PX}px;line-height:1.5}`
    + `.wrap{max-width:640px;margin:0 auto}`
    + `.eyebrow{font-size:11px;letter-spacing:.1em;text-transform:uppercase;color:#666;margin:0 0 6px}`
    // (1) branding band — organiser wordmark + event + accent rule
    + `.brand{border-bottom:3px solid #111;padding-bottom:12px;margin-bottom:20px}`
    + `.brand .mark{font-size:20px;font-weight:800;letter-spacing:-.01em;margin:0}`
    + `.brand .ev{font-size:13px;color:#555;margin:2px 0 0}`
    // CMT-011: event banner across the top (unchanged).
    + `.brand .banner{display:block;max-width:100%;height:auto;margin:0 0 4px;border-radius:6px}`
    // CMT-2026-09-03-024 (Duncan spec, BOARDROOM-2026-09-03-055847): RESERVED
    // FOOTER BAND on every printed page. The old bare position:fixed .pagelogo
    // let body text flow beneath it (the clause-5 collision on Duncan's
    // artifact). Overlap is now impossible BY CONSTRUCTION:
    //  (1) ALL flowed content sits in <table class=pageframe><tbody>; the
    //      <tfoot> holds the .bandspace spacer row. Print engines repeat the
    //      table-footer row group at the BOTTOM OF EVERY PAGE, so pagination
    //      itself reserves the bottom WAIVER_PRINT_BAND_SPACE_PX of each page —
    //      body content can only break ABOVE it, at any content length. No
    //      measurement, no overlay the text can flow under.
    //  (2) The visible .pageband is position:fixed (repeats per page) with
    //      height WAIVER_PRINT_BAND_PX < the spacer, overflow:hidden — it
    //      always paints wholly INSIDE the reserved zone and cannot outgrow it.
    //  (3) Belt-and-braces: the body keeps bottom padding >= the band in BOTH
    //      screen and print media, so on an engine that does not repeat tfoot
    //      the final page (receipt + signature) still ends clear of the band.
    + `.pageframe{width:100%;border-collapse:collapse}.pageframe td{padding:0}`
    + `.bandspace{height:${WAIVER_PRINT_BAND_SPACE_PX}px}`
    + `.pageband{position:fixed;left:0;right:0;bottom:0;height:${WAIVER_PRINT_BAND_PX}px;`
    + `overflow:hidden;box-sizing:border-box;display:flex;align-items:flex-end;`
    + `justify-content:space-between;gap:16px;padding:0 14px 12px}`
    // Sponsor slots — DESIGN CALL (CMT-024): LEFT-ALIGNED row (max 3) so the
    // band reads sponsors-left / Trumin-right with the centre kept clear; a
    // slot renders ONLY when an asset URL exists (empty today, assets later).
    + `.pageband .sponsors{display:flex;align-items:flex-end;gap:18px;min-width:0}`
    + `.pageband .sponsors img{max-height:44px;width:auto;object-fit:contain;`
    + `-webkit-print-color-adjust:exact;print-color-adjust:exact}`
    // BOARDROOM-2026-08-28-163605 (Duncan): the corner logo must be the ACTUAL
    // Trumin logo, print-strong — his B&W laser preview showed the old 66px
    // faint wordmark vanishing. 120px lockup, full opacity, darkened so its
    // greyscale conversion lands DARK (mid-teal alone prints as pale grey).
    // CMT-024: now anchored bottom-right INSIDE the band, clamped to fit it.
    + `.pagelogo{width:120px;height:auto;max-height:52px;object-fit:contain;flex:none;`
    + `margin-left:auto;opacity:1;`
    + `filter:brightness(.5) saturate(1.35);-webkit-print-color-adjust:exact;print-color-adjust:exact}`
    + `h1{font-size:22px;margin:20px 0 4px}h2{font-size:17px;margin:22px 0 2px}`
    + `h3{font-size:13.5px;margin:14px 0 6px}`
    + `.state{font-size:10px;font-weight:700;letter-spacing:.04em;text-transform:uppercase;`
    + `padding:2px 6px;border-radius:4px;vertical-align:middle;margin-left:6px}`
    + `.state.ok{background:#e7f6ec;color:#1a7f37}.state.no{background:#f3f4f6;color:#888}`
    + `.cl{display:flex;gap:8px;font-size:12.5px;margin:0 0 5px}`
    + `.cl.sub{margin-left:18px}.cl .n{color:#888;flex:none;min-width:34px}.cl .t{flex:1}`
    + `.note{font-size:11.5px;color:#666;margin:4px 0 0}`
    + `.doc+.doc{margin-top:8px;padding-top:8px;border-top:1px solid #eee}`
    + `.stamp{font-size:13px;color:#333;font-weight:700;margin:0 0 20px}`
    + `.receipt{margin-top:26px;padding-top:14px;border-top:2px solid #111}`
    // CMT-024: receipt-table rules SCOPED to .receipt — the whole doc now sits
    // inside a table cell (the pageframe), so bare th/td rules would restyle
    // (bold + right-align + hairline) the entire document.
    + `.receipt table{width:100%;border-collapse:collapse;font-size:13px}`
    + `.receipt th,.receipt td{text-align:left;vertical-align:top;padding:9px 0;border-bottom:1px solid #e2e2e2}`
    + `.receipt th{color:#666;font-weight:400;width:40%}.receipt td{font-weight:700;text-align:right}`
    + `.sig{margin-top:24px;padding-top:16px;border-top:1px solid #e2e2e2}`
    + `.sig .name{font-family:Georgia,'Times New Roman',serif;font-style:italic;font-size:20px}`
    + `.foot{margin-top:20px;font-size:11px;color:#888}`
    // CMT-024 belt-and-braces: keep bottom padding >= band in PRINT too.
    + `@media print{body{padding:0 0 ${WAIVER_PRINT_BAND_SPACE_PX}px}}`
    + `</style></head><body>`
    // CMT-024 PAGE FRAME — all flowed content in the tbody; the tfoot spacer
    // is what the print engine repeats at every page bottom (see CSS note).
    + `<table class="pageframe"><tbody><tr><td><div class="wrap">`
    // (1) branding header — CMT-011: the EVENT BANNER across the top (replacing
    // the Trumin wordmark, which is wrong for an event document). Falls back to
    // the organiser wordmark text when no banner URL is wired (never broken img).
    + `<div class="brand">`
    + (opts.eventBannerUrl
        ? `<img class="banner" src="${esc(opts.eventBannerUrl)}" alt="${esc(opts.event || 'Event')}">`
        : `<p class="mark">${esc(opts.organiser || 'Trumin')}</p>`)
    + (opts.event ? `<p class="ev">${esc(opts.event)}</p>` : '')
    + `</div>`
    // (2) the agreed legal document
    + legal
    // (3) receipt + signature evidence
    + `<div class="receipt">`
    + (header ? `<p class="eyebrow">${header}</p>` : '')
    + `<h1>Waiver signed</h1>`
    + `<p class="stamp">${esc(signedOnLine(record))} · timestamp and device recorded</p>`
    + `<table><tbody>${rows}</tbody></table>`
    + `<div class="sig"><div class="eyebrow">Signature</div>`
    + `<div class="name">${esc(record.signedBy)}</div></div>`
    + `<p class="foot">This is your signed record, kept in your Trumin account.</p>`
    + `</div>`
    + `</div></td></tr></tbody>`
    + `<tfoot><tr><td><div class="bandspace"></div></td></tr></tfoot></table>`
    // CMT-024 RESERVED FOOTER BAND — fixed, so it repeats at the bottom of
    // every printed page, painting inside the zone the tfoot spacer reserved.
    // Sponsor slots left (hidden until assets exist), Trumin mark bottom-right.
    + `<div class="pageband"><div class="sponsors">${sponsors}</div>`
    + (opts.truminLogoUrl ? `<img class="pagelogo" src="${esc(opts.truminLogoUrl)}" alt="Trumin">` : '')
    + `</div>`
    + `</body></html>`
}

/** Read-only tick derivation (canon §6: "ticks derived from the record") —
 *  required sections were necessarily accepted; optional ones accepted unless
 *  in the record's declined list. */
export function recordTick(
  section: { name: string; required: boolean },
  declined: string[],
): boolean {
  return section.required || !declined.includes(section.name)
}

/** Step counter [chrome]: `Step {n} of {N}`; the receipt (and any read-only
 *  re-open) reads `SIGNED`. N = docs + the sign step. */
export function stepCounter(stepIndex: number, docCount: number, signed: boolean, tr: TranslateFn = enT): string {
  return signed ? tr('waiver.stepnum.signed') : tr('flow.step', { n: stepIndex + 1, m: docCount + 1 })
}

/** Named-party line [chrome]: `{org} · {event}`. */
export function namedParty(template: WaiverTemplate): string {
  return `${template.organiser} · ${template.event}`
}
